<template>
  <div class="h-100">
    <!-- Outlined wherever it is used, filling its grid cell, as the deal and
         comparable create forms lay the role cards out in a two-column grid. -->
    <!-- The role card used across the app (property / availability Contacts cards):
         "Tenant Contact(s) (1)" with its own "+" in the title, the contacts below
         with dividers, a muted line when empty. Without a title (dense) it stays a
         bare list with a full-width add button. -->
    <v-card variant="outlined" class="h-100">
      <template v-if="hasHeader">
        <v-card-title class="d-flex align-center py-2 text-subtitle-1 font-weight-medium text-none" data-test="role-contacts-header">
          {{ roleTitle }}
          <span v-if="roleTitle" class="text-medium-emphasis ms-1">({{ (contacts || []).length }})</span>
          <v-spacer />
          <v-btn
            v-if="allowSave && (contacts || []).length && displayLock"
            icon
            size="small"
            variant="text"
            class="text-medium-emphasis"
            :aria-label="edit ? 'Lock contacts' : 'Unlock contacts'"
            data-track="contact.role-contacts.edit"
            @click="edit = !edit"
          >
            <v-icon>{{ edit ? 'mdi-lock-open' : 'mdi-lock' }}</v-icon>
          </v-btn>
          <v-btn
            v-if="showAddBtn"
            icon
            size="small"
            variant="text"
            class="mr-n2"
            :aria-label="`Add ${contactTypeLabel.toLowerCase()}`"
            data-test="role-contacts-add"
            data-track="contact.role-contacts.open"
            @click="openContactSelect"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text v-if="!(contacts || []).length" class="py-3 text-body-2 text-disabled">
          No {{ contactTypeLabel.toLowerCase() }} added
        </v-card-text>
      </template>
      <template v-for="(contact, index) in contacts" :key="contact._key">
        <v-divider v-if="index" />
        <ContactTile
          avatar
          :contact="contact"
          :source-type="source"
          :contact-role="role"
          :refresh-contact-edge="refreshContactEdge"
          expandable
          @removeContact="removeContact"
          @updateReferral="updateReferralHandler"
          @refresh:contact="$emit('refresh:contact', $event)"
        />
      </template>
      <v-card-text
        v-if="!hasHeader && showAddBtn"
        :class="{ 'text-center': buttonStyle === 'block', 'pa-0': buttonStyle === 'block' && !padded }"
      >
        <v-btn
          v-if="buttonStyle === 'block'"
          block
          size="x-large"
          variant="text"
          class="btn-add-contact"
          data-track="contact.role-contacts.open"
          @click="openContactSelect"
        >
          <v-icon start>mdi-plus</v-icon> Add {{ contactTypeLabel }}
        </v-btn>
        <v-btn
          v-else
          block
          data-track="contact.role-contacts.open"
          @click="openContactSelect"
        >
          <v-icon start>mdi-plus</v-icon> Add {{ contactTypeLabel }}
        </v-btn>
      </v-card-text>
    </v-card>
    <ConnectContact
      v-model="contactDialog"
      :query-by-role="queryByRole"
      :title="roleTitle !== '' ? roleTitle : title"
      :source="source"
      :use-company="useCompany"
      :role="role"
      :visibility="reset"
      :allow-multiple="allowMultiple"
      @connect="connectContacts"
      @close="contactDialog = false"
    />
  </div>
</template>

<script>
import ConnectContact from '@/components/common-components/connectContact/СonnectContact'
import ContactTile from '@/components/contacts/ContactTile.vue';

export default {
  name: 'RoleContactsCard',

  components: {
    ContactTile,
    ConnectContact,
  },

  props: {
    contacts: {
      type: Array,
      default: () => [],
    },
    role: {
      type: String,
      default: '',
    },
    roleTitle: {
      type: String,
      default: '',
    },
    id: {
      type: String,
      default: '',
    },
    allowSave: {
      type: Boolean,
      default: true,
    },
    allowAdd: {
      type: Boolean,
      default: true
    },
    contactTypeLabel: {
      type: String,
      default: 'Contact',
    },
    queryByRole: {
      type: String,
      default: '',
    },
    source: {
      type: String,
      default: '',
    },
    maxCount: {
      type: Number,
      default: -1,
    },
    useCompany: Boolean,
    buttonStyle: {
      type: String,
      default: 'block',
    },
    allowMultiple: {
      type: Boolean,
      default: true,
    },
    isDense: Boolean,
    padded: Boolean,
    displayLock: {
      type: Boolean,
      default: true
    },
    refreshContactEdge: {
      type: String,
      default: null,
    }
  },

  emits: ['refresh:contact', 'removeHandler', 'updateHandler', 'updateReferral'],

  data: () => ({
    edit: false,
    contactDialog: false,
    companyDialog: false,
    selectedContacts: [],
    reset: 0,
  }),

  computed: {
    hasHeader () {
      return (this.roleTitle !== '' || this.allowSave) && !this.isDense;
    },
    title () {
      return this.source === 'requirement' ? 'Connect Procuring Broker' : 'Add contact';
    },
    showAdd () {
      return (
        this.allowAdd && (this.maxCount === -1 ||
        (this.contacts !== null && this.maxCount > this.contacts?.length))
      )
    },
    showAddBtn () {
      const shouldShowAdd = (this.edit || !this.allowSave) && this.showAdd;
      const canAddNewContact = !this.contacts?.length || this.allowMultiple;

      return shouldShowAdd || canAddNewContact;
    }
  },

  methods: {
    connectContacts (contacts) {
      const addContacts = contacts.map((contact) => contact._id)
      const fullContacts = contacts.map((contact) => contact)
      const payload = {
        _id: this._id,
        role: this.role,
        contacts: addContacts,
        contactDetails: fullContacts,
        action: 'addContacts',
      }
      this.$emit('updateHandler', payload)
      this.contactDialog = false
    },
    removeCompany (company) {
      const payload = {
        _id: this._id,
        role: this.role,
        contact: company._id,
        action: 'remove',
      }

      this.$emit('removeHandler', payload)
    },
    removeContact (contactKey) {
      const payload = {
        contact: this.contacts.find((contact) => contact._key === contactKey),
        role: this.role
      }

      this.$emit('removeHandler', payload)
    },
    openContactSelect () {
      this.reset++
      this.contactDialog = true
    },
    updateReferralHandler (value) {
      const payload = {
        _edge: value._edge,
        id: value.contactId,
        referring_agent: (value.referral === 'referring'),
        receiving_agent: (value.referral === 'receiving')
      }
      this.$emit('updateReferral', payload)
    }
  },
}
</script>
